import { ZodError } from 'zod';

export interface ManifestError {
  code:
    | 'schema_version_missing'
    | 'unknown_status'
    | 'insecure_url'
    | 'http_localhost_rejected'
    | 'unknown_shape'
    | 'parse_failed';
  message: string;
  field?: string;
  cause?: unknown;
}

function inferFieldFromZodPath(input: unknown): string | undefined {
  if (input instanceof ZodError && input.issues.length > 0) {
    const issue = input.issues[0];
    if (issue && issue.path.length > 0) {
      return String(issue.path[issue.path.length - 1]);
    }
  }
  return undefined;
}

export function toManifestError(input: unknown): ManifestError {
  const message =
    typeof input === 'object' && input !== null && input instanceof Error
      ? input.message
      : String(input);

  const cause = input instanceof Error ? input : undefined;
  const zodField = inferFieldFromZodPath(input);
  const lowerMsg = message.toLowerCase();

  if (lowerMsg.includes('schemaversion') && lowerMsg.includes('required')) {
    return {
      code: 'schema_version_missing',
      message: 'Invalid manifest: schema version is missing or invalid',
      field: zodField ?? 'schemaVersion',
      cause,
    };
  }

  if (lowerMsg.includes('status must be') || lowerMsg.includes('app status')) {
    return {
      code: 'unknown_status',
      message: 'Invalid manifest: app status must be "active" or "disabled"',
      field: zodField ?? 'status',
      cause,
    };
  }

  if (lowerMsg.includes('http rejected for host')) {
    const hostname = (lowerMsg.match(/host:\s*([^\s)\]]+)/)?.[1] ?? 'unknown').replace(
      /[)\]>,;]+$/,
      '',
    );
    const isLocalLooking =
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '::1' ||
      hostname.endsWith('.local') ||
      hostname.endsWith('.localhost');
    if (isLocalLooking) {
      return {
        code: 'http_localhost_rejected',
        message: `Invalid manifest: HTTP for ${hostname} is not allowlisted in this context`,
        field: zodField ?? 'url',
        cause,
      };
    }
    return {
      code: 'insecure_url',
      message: 'Invalid manifest: app URL must use HTTPS',
      field: zodField ?? 'url',
      cause,
    };
  }

  if (lowerMsg.includes('must use https')) {
    return {
      code: 'insecure_url',
      message: 'Invalid manifest: app URL must use HTTPS',
      field: zodField ?? 'url',
      cause,
    };
  }

  if (input instanceof ZodError || lowerMsg.includes('zoderror')) {
    return {
      code: 'parse_failed',
      message: 'Invalid manifest format',
      field: zodField,
      cause,
    };
  }

  return {
    code: 'unknown_shape',
    message: 'Unexpected error loading manifest',
    field: undefined,
    cause,
  };
}
